import { useEffect, useMemo, useState, useRef } from "react";
import { Sparkles, ArrowRight, Award, Shield, Gem, Zap } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [enableParallax, setEnableParallax] = useState(false);
  const { contactInfo, sectionSettings } = useSettings();
  const sectionRef = useRef(null);
  const rafRef = useRef(null);

  const particleConfig = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => {
        const sizeClass =
          i % 3 === 0 ? "w-1 h-1" : i % 3 === 1 ? "w-1.5 h-1.5" : "w-2.5 h-2.5";
        const opacityClass =
          i % 4 === 0
            ? "bg-[#D4AF7A]/20"
            : i % 4 === 1
              ? "bg-[#D4AF7A]/40"
              : i % 4 === 2
                ? "bg-[#D4AF7A]/50"
                : "bg-[#E5C99A]/30";
        return {
          id: i,
          left: `${Math.random() * 100}%`,
          animationDelay: `${i * 0.4}s`,
          animationDuration: `${18 + Math.random() * 14}s`,
          sizeClass,
          opacityClass,
        };
      }),
    [],
  );

  const sparkleDots = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        id: `sparkle-${i}`,
        top: `${20 + Math.random() * 60}%`,
        left: `${20 + Math.random() * 60}%`,
        size: 2 + Math.random() * 3,
        delay: `${i * 0.7}s`,
        duration: `${3 + Math.random() * 3}s`,
      })),
    [],
  );

  // Mouse move effect for parallax
  useEffect(() => {
    const mediaQueryCoarse = window.matchMedia("(pointer: coarse)");
    const mediaQueryReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    const shouldEnable =
      !mediaQueryCoarse.matches && !mediaQueryReduced.matches;
    setEnableParallax(shouldEnable);

    if (!shouldEnable) {
      setMousePosition({ x: 0, y: 0 });
      return undefined;
    }

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;

      if (rafRef.current) return;

      rafRef.current = window.requestAnimationFrame(() => {
        setMousePosition((prev) => {
          if (Math.abs(prev.x - x) < 0.2 && Math.abs(prev.y - y) < 0.2) {
            return prev;
          }
          return { x, y };
        });
        rafRef.current = null;
      });
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Initial visibility
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const features = [
    { icon: <Sparkles size={20} />, text: "Custom Design" },
    { icon: <Award size={20} />, text: "Detail Elegan" },
    { icon: <Gem size={20} />, text: "Sentuhan Eksklusif" },
    { icon: <Shield size={20} />, text: "Bersertifikat" },
  ];

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative flex items-center justify-center min-h-screen overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="./images/hero.png"
          alt="Henna Background"
          fetchPriority="high"
          className="object-cover w-full h-full lg:hidden"
          style={{
            transform: enableParallax
              ? `scale(1.1) translate(${mousePosition.x * -0.5}px, ${mousePosition.y * -0.5}px)`
              : "scale(1.1)",
            transition: "transform 0.2s ease-out",
            willChange: enableParallax ? "transform" : "auto",
          }}
        />
        <img
          src="./images/hero.webp"
          alt="Henna Background"
          fetchPriority="high"
          className="hidden object-cover w-full h-full lg:block"
          style={{
            transform: enableParallax
              ? `scale(1.1) translate(${mousePosition.x * -0.5}px, ${mousePosition.y * -0.5}px)`
              : "scale(1.1)",
            transition: "transform 0.2s ease-out",
            willChange: enableParallax ? "transform" : "auto",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90"></div>
      </div>

      {/* Gold Radial Gradient Behind Content */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] max-w-full max-h-full">
        <div className="w-full h-full bg-gradient-radial from-[#D4AF7A]/8 via-[#C9A882]/4 to-transparent rounded-full"></div>
      </div>

      {/* Floating Decorative Elements */}
      <div
        className="absolute top-20 right-10 w-72 h-72 bg-[#D4AF7A]/10 rounded-full blur-3xl animate-float"
        style={{ animationDuration: "8s" }}
      ></div>
      <div
        className="absolute bottom-20 left-10 w-96 h-96 bg-[#C9A882]/10 rounded-full blur-3xl animate-float"
        style={{ animationDuration: "8s", animationDelay: "1s" }}
      ></div>
      <div
        className="absolute top-1/3 left-1/4 w-48 h-48 bg-[#E5C99A]/8 rounded-full blur-3xl animate-morph"
        style={{ animationDuration: "10s", animationDelay: "2s" }}
      ></div>

      {/* Animated Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particleConfig.map((particle) => (
          <div
            key={particle.id}
            className={`absolute rounded-full animate-particle ${particle.sizeClass} ${particle.opacityClass}`}
            style={{
              left: particle.left,
              animationDelay: particle.animationDelay,
              animationDuration: particle.animationDuration,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
        <div className={isVisible ? "opacity-100" : "opacity-0"}>
          {/* Logo with scale and float animation */}
          <div
            className="flex justify-center mb-4 sm:mb-8"
            style={{
              transform: enableParallax
                ? `translateY(${mousePosition.y * 0.3}px)`
                : "translateY(0px)",
              transition: "transform 0.2s ease-out",
              willChange: enableParallax ? "transform" : "auto",
            }}
          >
            <div className="relative group">
              {/* Morphing ornamental blob behind logo */}
              <div className="absolute inset-0 w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 -top-6 -left-6 sm:-top-8 sm:-left-8 md:-top-10 md:-left-10 mx-auto bg-gradient-to-br from-[#D4AF7A]/15 via-[#C9A882]/10 to-transparent rounded-full animate-morph blur-sm"></div>

              {/* Gold glow background */}
              <div className="absolute inset-0 bg-[#D4AF7A]/20 rounded-full blur-xl group-hover:bg-[#D4AF7A]/30 transition-all duration-500 animate-glow"></div>

              {/* Decorative gold ring around logo */}
              <div className="absolute inset-0 w-36 h-36 sm:w-48 sm:h-48 md:w-64 md:h-64 -top-1.5 -left-1.5 sm:-top-2 sm:-left-2 md:-top-2 md:-left-2 rounded-full border border-[#D4AF7A]/30 animate-spin-slow shadow-[0_0_30px_rgba(212,175,122,0.15)]"></div>
              <div
                className="absolute inset-0 w-32 h-32 sm:w-44 sm:h-44 md:w-60 md:h-60 top-0 left-0 sm:top-0 sm:left-0 md:top-0 md:left-0 rounded-full border border-[#D4AF7A]/10 animate-spin-slow shadow-[0_0_20px_rgba(212,175,122,0.1)]"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "25s",
                }}
              ></div>

              {/* Sparkle dots around logo */}
              {sparkleDots.map((dot) => (
                <div
                  key={dot.id}
                  className="absolute w-1 h-1 bg-[#D4AF7A] rounded-full animate-pulse"
                  style={{
                    top: dot.top,
                    left: dot.left,
                    width: `${dot.size}px`,
                    height: `${dot.size}px`,
                    animationDelay: dot.delay,
                    animationDuration: dot.duration,
                  }}
                />
              ))}

              <img
                src="./logo512.png"
                alt="Putri Henna Artist Logo"
                className="relative w-auto h-32 transition-transform duration-500 sm:h-40 md:h-60 group-hover:scale-110 animate-logo-float"
              />
            </div>
          </div>

          {/* Decorative gold divider before heading */}
          <div
            className="flex items-center justify-center gap-3 mb-4 animate-slide-down"
            style={{ animationDelay: "0.1s", animationFillMode: "both" }}
          >
            <div className="w-16 divider-gold sm:w-24"></div>
            <span className="text-[#D4AF7A] text-base sm:text-lg md:text-xl font-serif">
              &diams;
            </span>
            <div className="w-16 divider-gold sm:w-24"></div>
          </div>

          {/* Heading with staggered animation */}
          <h1 className="mb-6">
            <span
              className="block font-pacifico text-3xl sm:text-4xl md:text-6xl font-bold text-[#F7F5F2] mb-3 animate-slide-down"
              style={{ animationDelay: "0.2s", animationFillMode: "both" }}
            >
              Sentuhan Henna
            </span>
            <span
              className="block text-xl font-semibold tracking-wide font-display sm:text-2xl md:text-4xl lg:text-5xl animate-slide-down"
              style={{ animationDelay: "0.35s", animationFillMode: "both" }}
            >
              <span className="animate-text-shimmer bg-[length:200%_100%]">
                dalam Harmoni Rasa & Estetika
              </span>
            </span>
          </h1>

          {/* Decorative separator */}
          <div
            className="flex items-center justify-center gap-2 mb-6 animate-fade-in"
            style={{ animationDelay: "0.5s", animationFillMode: "both" }}
          >
            <div className="w-2 h-2 bg-[#D4AF7A]/40 rounded-full"></div>
            <div className="w-12 h-px bg-gradient-to-r from-[#D4AF7A]/60 via-[#E5C99A] to-[#D4AF7A]/60"></div>
            <span className="text-[#E5C99A] text-xl font-light">&#10022;</span>
            <div className="w-12 h-px bg-gradient-to-r from-[#D4AF7A]/60 via-[#E5C99A] to-[#D4AF7A]/60"></div>
            <div className="w-2 h-2 bg-[#D4AF7A]/40 rounded-full"></div>
          </div>

          {/* Subheading with fade-in animation */}
          <p
            className="text-sm sm:text-base md:text-lg text-[#F7F5F2]/80 max-w-3xl mx-auto mb-8 sm:mb-10 leading-relaxed animate-fade-in"
            style={{ animationDelay: "0.6s", animationFillMode: "both" }}
          >
            Menyediakan layanan henna khusus acara pernikahan, mulai dari henna
            wedding untuk pengantin hingga henna fun untuk bridesmaid, pagar
            ayu, dan keluarga pengantin. Hadir untuk mempercantik momen bahagia
            Anda dengan sentuhan henna yang manis dan berkesan.
          </p>

          {/* Decorative gold quote line */}
          <div
            className="flex items-center justify-center gap-2 mb-6 animate-fade-in"
            style={{ animationDelay: "0.7s", animationFillMode: "both" }}
          >
            <span className="text-[#D4AF7A]/40 text-2xl font-serif leading-none">
              &ldquo;
            </span>
            <div className="w-8 h-px bg-[#D4AF7A]/20"></div>
            <span className="text-[#D4AF7A]/60 text-xs sm:text-sm font-medium italic tracking-wider uppercase">
              Keunggulan Kami
            </span>
            <div className="w-8 h-px bg-[#D4AF7A]/20"></div>
            <span className="text-[#D4AF7A]/40 text-2xl font-serif leading-none">
              &rdquo;
            </span>
          </div>

          {/* Features with stagger animation */}
          <div className="flex flex-wrap justify-center gap-4 mb-10 sm:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-[#D4AF7A]/30 hover:bg-[#D4AF7A]/10 hover:border-[#D4AF7A]/60 hover:scale-110 hover:shadow-[0_0_20px_rgba(212,175,122,0.2)] transition-all duration-300 animate-slide-up cursor-default group"
                style={{
                  animationDelay: `${0.8 + index * 0.12}s`,
                  animationFillMode: "both",
                }}
              >
                <span className="text-[#D4AF7A] group-hover:rotate-12 group-hover:text-[#E5C99A] transition-all duration-300">
                  {feature.icon}
                </span>
                <span className="text-[#F7F5F2] text-sm sm:text-base font-medium group-hover:text-white transition-colors duration-300">
                  {feature.text}
                </span>
              </div>
            ))}
          </div>

          {/* CTA Buttons with animation */}
          <div
            className="flex flex-wrap items-center justify-center gap-4 animate-slide-up"
            style={{ animationDelay: "1.2s", animationFillMode: "both" }}
          >
            <a
              href={`https://wa.me/${contactInfo.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group btn-gold-premium btn-magnetic px-4 py-2.5 sm:px-6 sm:py-3.5 text-black font-bold rounded-full flex items-center gap-2 btn-icon-slide text-sm sm:text-base hover:shadow-[0_0_30px_rgba(212,175,122,0.5)]"
            >
              <Zap
                size={20}
                className="transition-transform duration-300 group-hover:rotate-12"
              />
              Konsultasi Gratis
              <ArrowRight
                size={20}
                className="transition-transform group-hover:translate-x-1 icon-slide"
              />
            </a>
            {sectionSettings.gallery !== false && (
              <button
                onClick={() => scrollToSection("gallery")}
                className="group btn-gold-outline px-4 py-2.5 sm:px-5 sm:py-3.5 font-bold rounded-full text-sm sm:text-base flex items-center gap-2"
              >
                Lihat Portfolio
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scroll Indicator with bounce */}
      <div className="absolute hidden transform -translate-x-1/2 bottom-8 left-1/2 animate-bounce lg:block">
        <button
          onClick={() => scrollToSection("about")}
          className="flex flex-col items-center gap-2 cursor-pointer group"
        >
          <div className="w-6 h-10 border-2 border-[#D4AF7A] rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-[#D4AF7A] rounded-full mt-2 animate-scroll"></div>
          </div>
          <span className="text-[#F7F5F2]/60 text-xs group-hover:text-[#D4AF7A] transition-colors">
            Scroll
          </span>
        </button>
      </div>
    </section>
  );
};

export default Hero;
